import React from 'react';

import YearsAndMonths from 'components/Generic/YearsAndMonths';
import { sqlDateToLabel, formatAmount, fractionToPercent } from 'helpers/helpers';
import Grid from '@material-ui/core/Grid';
import H2 from 'apps/Pdf/components/common/H2';
import { rentIsZero } from 'helpers/targets';

import useGeneralStyles from 'apps/Pdf/styles/general';
import { RentData } from 'stores/workflowStores/finances/rent/model';
import { CalculationMode, AmountValue } from 'common/interfaces';
import { PersonType, Currency } from 'enums';

interface Props {
  amountAtRelevantDate?: AmountValue;
  rentApplied: boolean;
  rentData: RentData;
  currency: Currency;
  inflation: { enabled: boolean; rate: number };
  personType: PersonType;
  annual?: boolean;
  actualInflation: number;
}

const Rent = (props: Props) => {
  const styles = useGeneralStyles();
  const { currency, inflation, annual, actualInflation } = props;

  return rentIsZero(props.rentData) ? null : (
    <>
      <H2>
        Očekávaný vývoj hodnoty majetku v čase&nbsp;
        {inflation.enabled ? 'v současných (reálných) hodnotách' : 'v budoucích (nominálních) hodnotách'}
      </H2>
      <div className={`${styles.threeColumns} ${styles.center} ${styles.lines}`}>
        <Grid item xs={12}>
          Začátek čerpání renty
        </Grid>
        <Grid item xs={12}>
          {props.amountAtRelevantDate &&
            (props.rentApplied ? 'Celková hodnota majetku při začátku čerpání renty' : 'Hodnota za 10 let')}
        </Grid>
        <Grid item xs={12}>
          Výše měsíční renty
        </Grid>
      </div>

      <div className={`${styles.threeColumns} ${styles.center} ${styles.lines}`}>
        <Grid item xs={12} className={styles.strongText}>
          {sqlDateToLabel(props.rentData.start, 'MM/yyyy')}
        </Grid>
        <Grid item xs={12} className={styles.strongText}>
          {props.amountAtRelevantDate && formatAmount(props.amountAtRelevantDate, currency)}
        </Grid>
        <Grid item xs={12}>
          <span className={styles.strongText}>
            {props.rentData.portfolioRentCalculationMode === CalculationMode.CALCULATED &&
              formatAmount(props.rentData.calculatedPortfolioRent, currency)}
            {props.rentData.portfolioRentCalculationMode === CalculationMode.MANUAL &&
              formatAmount(props.rentData.plannedPortfolioRent, currency)}
            {props.rentData.portfolioRentCalculationMode === CalculationMode.REQUIRED &&
              formatAmount(props.rentData.requiredPortfolioRent, currency)}
          </span>
          <br />(v reálných cenách)
          <br />
          {props.rentData.durabilityUntil ? (
            props.personType === PersonType.PHYSICAL ? (
              <>
                do <YearsAndMonths data={props.rentData.durability} />
                {props.rentData.partnerDurability && (
                  <>
                    {' '}
                    / <YearsAndMonths data={props.rentData.partnerDurability} />
                  </>
                )}
                {props.rentData.partnerDurability ? <br /> : <>&nbsp;</>}
                (věk klienta{props.rentData.partnerDurability && <> / partnera</>})
              </>
            ) : (
              <>do {sqlDateToLabel(props.rentData.durabilityUntil)}</>
            )
          ) : props.personType === PersonType.LEGAL ? (
            'do konce plánu'
          ) : (
            'doživotně'
          )}
        </Grid>
      </div>
      {annual && props.rentData.inflation.valuation && (
        <div className={styles.center}>
          <strong>
            Částka renty se automaticky navyšuje podle{' '}
            {props.rentData.inflation.type === 'ACTUAL' ? 'skutečné' : 'nastavené'} inflace{' '}
            {fractionToPercent(
              props.rentData.inflation.type === 'PLANNED' ? props.rentData.inflation.rate : actualInflation,
            )}
          </strong>
        </div>
      )}
    </>
  );
};

export default Rent;
